import { chatAttachmentUrl } from '../../utils/chatAttachment';
import React, { useEffect, useRef, useState, useContext } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { IconButton, Box, Typography } from '@mui/material';
import { PlayArrow, Pause, Mic } from '@mui/icons-material';
import { ThemeContext } from '../ThemeContext/ThemeContext';

const CustomAudioPlayer = ({ archive, roomId }) => {
  const waveformRef = useRef(null);
  const wavesurfer = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const { theme } = useContext(ThemeContext);
  const cursorRef = useRef(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!archive) return;

    let audioUrl;

    const isBase64 = archive.startsWith('data:audio');

    if (isBase64) {
      const base64Data = archive.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length).fill().map((_, i) => byteCharacters.charCodeAt(i));
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'audio/ogg; codecs=opus' });
      audioUrl = URL.createObjectURL(blob);
    } else {
      audioUrl = archive.startsWith('http')
        ? archive
        : chatAttachmentUrl(roomId, archive);
    }

    wavesurfer.current = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: theme.palette.primary.main,
      progressColor: theme.palette.background.secondary,
      cursorColor: 'transparent',
      cursorWidth: 0,
      barWidth: 2,
      height: 50,
      responsive: true,
      interact: true,
    });

    const updateTimeAndCursor = () => {
      if (!wavesurfer.current) return;
      const dur = wavesurfer.current.getDuration();
      const current = wavesurfer.current.getCurrentTime();
      setCurrentTime(current);
      setDuration(dur);

      const container = waveformRef.current;
      const cursor = cursorRef.current;
      if (container && cursor && dur > 0) {
        const width = container.offsetWidth;
        const left = (current / dur) * width;
        cursor.style.left = `${left}px`;
      }
    };

    wavesurfer.current.on('audioprocess', updateTimeAndCursor);
    wavesurfer.current.on('seek', () => {
      setTimeout(updateTimeAndCursor, 0);
    });
    wavesurfer.current.on('interaction', () => {
      const current = wavesurfer.current.getCurrentTime();
      const duration = wavesurfer.current.getDuration();
      const container = waveformRef.current;
      const cursor = cursorRef.current;

      if (container && cursor) {
        const width = container.offsetWidth;
        const left = (current / duration) * width;

        setCurrentTime(current);
        cursor.style.left = `${left}px`;
      }
    });

    wavesurfer.current.on('ready', updateTimeAndCursor);
    wavesurfer.current.on('finish', () => setPlaying(false));
    wavesurfer.current.on('error', (err) => {
      console.error('Falha ao carregar áudio:', err);
      setLoadError(true);
    });

    wavesurfer.current.load(audioUrl).catch((err) => {
      console.error('Falha ao carregar áudio:', err);
      setLoadError(true);
    });

    return () => {
      wavesurfer.current.destroy();
      if (isBase64) URL.revokeObjectURL(audioUrl);
    };
  }, [archive, theme]);

  const togglePlay = () => {
    if (wavesurfer.current) {
      wavesurfer.current.playPause();
      setPlaying(!playing);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '14.75rem',
        padding: '0.5rem 1rem',
        borderRadius: '12px',
        backgroundColor: theme.palette.background.default,
        flexDirection: 'row',
        position: 'relative'
      }}
    >
      <Mic />
      {loadError ? (
        <Typography variant="caption" color="textSecondary">
          Áudio indisponível
        </Typography>
      ) : (
        <>
          <IconButton onClick={togglePlay}>
            {playing ? <Pause fontSize='small' /> : <PlayArrow fontSize='small' />}
          </IconButton>

          <Box sx={{ flexGrow: 1, position: 'relative' }}>
            <div ref={waveformRef} style={{ width: '100%', height: '50px' }} />
            <Box
              ref={cursorRef}
              sx={{
                position: 'absolute',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: theme.palette.primary.main,
                pointerEvents: 'none',
                transition: 'left 0.05s linear',
              }}
            />
          </Box>

          <Box sx={{ minWidth: '60px', textAlign: 'center' }}>
            <Typography variant="caption" color="textSecondary">
              {playing ? formatTime(currentTime) : formatTime(duration)}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

export default CustomAudioPlayer;
